import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import {
  userActivityApi,
  type UserActivityStats,
  type UserEvent,
} from '../../../services/birdbrain-api-client'
import { errorMessage } from '@/lib/utils'

// Types
interface UserActivityData {
  events: UserEvent[]
  stats: UserActivityStats | null
  totalCount: number
  hasMore: boolean
  page: number
}

interface DailyActivityData {
  dates: string[]
  series: Record<string, number[]>
}

interface UserActivityState {
  userActivity: Record<string, UserActivityData>
  dailyActivity: Record<string, DailyActivityData>
  eventNames: Record<string, string[]>
  loading: Record<string, boolean>
  loadingMore: Record<string, boolean>
  loadingDailyActivity: Record<string, boolean>
  loadingEventNames: Record<string, boolean>
  fetched: Record<string, boolean>
  fetchedDailyActivity: Record<string, boolean>
  error: string | null
  /** Per-user daily-rollup failure — lets the Usage chart tell "no data" from "source broke". */
  dailyActivityError: Record<string, string | null>
}

const initialState: UserActivityState = {
  userActivity: {},
  dailyActivity: {},
  eventNames: {},
  loading: {},
  loadingMore: {},
  loadingDailyActivity: {},
  loadingEventNames: {},
  fetched: {},
  fetchedDailyActivity: {},
  error: null,
  dailyActivityError: {},
}

// Async thunks
export const fetchUserActivity = createAsyncThunk(
  'userActivity/fetchUserActivity',
  async (
    params: {
      userId: string
      page?: number
      limit?: number
      eventName?: string
      append?: boolean
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await userActivityApi.getUserActivity({
        userId: params.userId,
        page: params.page || 1,
        limit: params.limit || 50,
        eventName: params.eventName,
      })
      return { ...response, append: params.append || false }
    } catch (error) {
      return rejectWithValue({
        message: errorMessage(error) || 'Failed to fetch user activity',
      })
    }
  }
)

export const fetchUserEventNames = createAsyncThunk(
  'userActivity/fetchUserEventNames',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await userActivityApi.getUserEventNames(userId)
      return response
    } catch (error) {
      return rejectWithValue({
        message: errorMessage(error) || 'Failed to fetch event names',
      })
    }
  }
)

export const fetchDailyActivity = createAsyncThunk(
  'userActivity/fetchDailyActivity',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await userActivityApi.getDailyActivity(userId)
      return { userId, ...response }
    } catch (error) {
      return rejectWithValue({
        message: errorMessage(error) || 'Failed to fetch daily activity',
      })
    }
  }
)

// Slice
const userActivitySlice = createSlice({
  name: 'userActivity',
  initialState,
  reducers: {
    clearUserActivity: (state, action: PayloadAction<string>) => {
      delete state.userActivity[action.payload]
      delete state.eventNames[action.payload]
    },
    clearError: state => {
      state.error = null
    },
  },
  extraReducers: builder => {
    // Fetch user activity
    builder
      .addCase(fetchUserActivity.pending, (state, action) => {
        const { userId, append } = action.meta.arg
        if (append) {
          state.loadingMore[userId] = true
        } else {
          state.loading[userId] = true
        }
        state.error = null
      })
      .addCase(fetchUserActivity.fulfilled, (state, action) => {
        const { userId, events, stats, totalCount, hasMore, page, append } = action.payload

        if (append) {
          state.loadingMore[userId] = false
          // Append events to existing
          const existing = state.userActivity[userId]
          if (existing) {
            state.userActivity[userId] = {
              ...existing,
              events: [...existing.events, ...events],
              hasMore,
              page,
            }
          }
        } else {
          state.loading[userId] = false
          state.fetched[userId] = true
          state.userActivity[userId] = {
            events,
            stats,
            totalCount,
            hasMore,
            page,
          }
        }
      })
      .addCase(fetchUserActivity.rejected, (state, action) => {
        const { userId, append } = action.meta.arg
        if (append) {
          state.loadingMore[userId] = false
        } else {
          state.loading[userId] = false
          state.fetched[userId] = true // Set fetched to true even on error to prevent infinite retry
        }
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch user activity'
      })

    // Fetch event names
    builder
      .addCase(fetchUserEventNames.pending, (state, action) => {
        state.loadingEventNames[action.meta.arg] = true
      })
      .addCase(fetchUserEventNames.fulfilled, (state, action) => {
        const { userId, eventNames } = action.payload
        state.loadingEventNames[userId] = false
        state.eventNames[userId] = eventNames
      })
      .addCase(fetchUserEventNames.rejected, (state, action) => {
        state.loadingEventNames[action.meta.arg] = false
      })

    // Fetch daily activity
    builder
      .addCase(fetchDailyActivity.pending, (state, action) => {
        state.loadingDailyActivity[action.meta.arg] = true
        state.dailyActivityError[action.meta.arg] = null
      })
      .addCase(fetchDailyActivity.fulfilled, (state, action) => {
        const { userId, dates, series } = action.payload
        state.loadingDailyActivity[userId] = false
        state.fetchedDailyActivity[userId] = true
        state.dailyActivity[userId] = { dates, series }
        state.dailyActivityError[userId] = null
      })
      .addCase(fetchDailyActivity.rejected, (state, action) => {
        state.loadingDailyActivity[action.meta.arg] = false
        state.fetchedDailyActivity[action.meta.arg] = true // Set fetched to true even on error
        state.dailyActivityError[action.meta.arg] =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch daily activity'
      })
  },
})

export const { clearUserActivity, clearError: clearActivityError } = userActivitySlice.actions
export const userActivityReducer = userActivitySlice.reducer

// Base selectors
const selectUserActivityState = (state: { userActivity: UserActivityState }) => state.userActivity
const selectUserActivityMap = (state: { userActivity: UserActivityState }) =>
  state.userActivity.userActivity
const selectEventNamesMap = (state: { userActivity: UserActivityState }) =>
  state.userActivity.eventNames
const selectLoadingMap = (state: { userActivity: UserActivityState }) => state.userActivity.loading
const selectLoadingMoreMap = (state: { userActivity: UserActivityState }) =>
  state.userActivity.loadingMore

// Memoized selectors
export const selectUserActivityEvents = (userId: string) =>
  createSelector([selectUserActivityMap], activityMap => activityMap[userId]?.events || [])

export const selectUserActivityStats = (userId: string) =>
  createSelector([selectUserActivityMap], activityMap => activityMap[userId]?.stats || null)

export const selectUserActivityHasMore = (userId: string) =>
  createSelector([selectUserActivityMap], activityMap => activityMap[userId]?.hasMore || false)

export const selectUserActivityPage = (userId: string) =>
  createSelector([selectUserActivityMap], activityMap => activityMap[userId]?.page || 1)

export const selectUserActivityLoading = (userId: string) =>
  createSelector([selectLoadingMap], loadingMap => loadingMap[userId] || false)

export const selectUserActivityLoadingMore = (userId: string) =>
  createSelector([selectLoadingMoreMap], loadingMoreMap => loadingMoreMap[userId] || false)

export const selectUserEventNames = (userId: string) =>
  createSelector([selectEventNamesMap], eventNamesMap => eventNamesMap[userId] || [])

// Fetched selectors
const selectFetchedMap = (state: { userActivity: UserActivityState }) => state.userActivity.fetched
const selectFetchedDailyActivityMap = (state: { userActivity: UserActivityState }) =>
  state.userActivity.fetchedDailyActivity

export const selectUserActivityFetched = (userId: string) =>
  createSelector([selectFetchedMap], fetchedMap => fetchedMap[userId] || false)

export const selectDailyActivityFetched = (userId: string) =>
  createSelector([selectFetchedDailyActivityMap], fetchedMap => fetchedMap[userId] || false)

// Daily activity selectors
const selectDailyActivityMap = (state: { userActivity: UserActivityState }) =>
  state.userActivity.dailyActivity
const selectLoadingDailyActivityMap = (state: { userActivity: UserActivityState }) =>
  state.userActivity.loadingDailyActivity

export const selectDailyActivity = (userId: string) =>
  createSelector(
    [selectDailyActivityMap],
    dailyActivityMap => dailyActivityMap[userId] || { dates: [], series: {} }
  )

export const selectDailyActivityLoading = (userId: string) =>
  createSelector([selectLoadingDailyActivityMap], loadingMap => loadingMap[userId] || false)

const selectDailyActivityErrorMap = (state: { userActivity: UserActivityState }) =>
  state.userActivity.dailyActivityError

export const selectDailyActivityError = (userId: string) =>
  createSelector([selectDailyActivityErrorMap], errorMap => errorMap[userId] || null)

export const selectUserActivityError = createSelector(
  [selectUserActivityState],
  state => state.error
)
